import {
  DEFAULT_PASSENGER_CATEGORY,
  OVERBOOKED_PASSENGER_CATEGORY,
} from '../../constants/passengerCategories';
import { isBfoPricing } from '../purchase/baseFareOverride';
import { roundTripFareApplies } from '../roundTripFare';
import { netFeePerPassenger } from '../trips/displayedPrice';
import {
  finalTotalBase,
  isPrice,
  markupDelta,
  resolveStrikethroughTotal,
} from '../trips/finalPricing';
import { PRICE_ARMS, priceForArm } from '../trips/priceArm';

/**
 * The price derivation of a search result card (`TripItem`), as pure functions.
 *
 * A card paints up to four prices for one trip -- the headline total, the logged-in `totalBase`,
 * the crossed-out previous price and the ticket-office fare -- and derives a discount percentage
 * from them. Every one of them has to come from the same price arm (control, INS-1288's netted
 * total or BOOK-168's markup-inclusive total): a card that mixes a netted or final headline with a
 * gross figure beside it advertises a discount that does not exist. Deriving them here keeps that
 * invariant assertable without mounting the card, which needs a store, a router and a dozen hooks.
 *
 * The derivation runs in two steps because the arm depends on it: the winning candidate is chosen
 * first (by `total`, the same in every arm), the readers are gated on that candidate, and only then
 * are the figures painted for the arm they resolved.
 */

/**
 * The discount amount to put beside a total painted in the given arm.
 *
 * A card or badge that has no backend percentage derives one as `amount / (total + amount)`. In
 * the final arm the total carries the markup, so the amount has to come from the markup-inclusive
 * pair too -- `totalBeforeDiscountWithMarkup - totalWithMarkup` -- or the badge understates the
 * discount (a base 75 over a final 621 reads 11% where control reads 12%, BOOK-168 AE3). Netting
 * never touches the amount (INS-1288 treats it as a delta, not a price), so control and netted
 * return it as sent.
 *
 * When the pair is missing, not numeric or inverted, the amount stays as sent: it is the same one
 * `resolveStrikethroughTotal` adds back to the painted total to rebuild the previous price, so the
 * badge and the crossed-out price still agree with each other. A leg without a discount (`0`,
 * `null`, `undefined`) never gains one.
 *
 * @param {object} [pricing] - The pricing object that carries the discount.
 * @param {number} [amount] - The discount amount as the backend sent it.
 * @param {string} [arm] - One of `PRICE_ARMS`.
 * @returns {number|null|undefined} The amount on the scale of the arm's total.
 */
export const discountAmountForArm = (pricing, amount, arm = PRICE_ARMS.CONTROL) => {
  if (arm !== PRICE_ARMS.FINAL || !amount) return amount;

  const before = pricing?.totalBeforeDiscountWithMarkup;
  const after = pricing?.totalWithMarkup;
  if (!isPrice(before) || !isPrice(after) || Number(before) <= Number(after)) return amount;

  return Number(before) - Number(after);
};

/**
 * The pricing object a card reads for its leg.
 *
 * The list resolves coupon/wallet and validated-return pricing before rendering the card, and
 * that pricing wins. Direct callers retain the leg rule and round-trip validation below.
 *
 * @param {object} params - Selection inputs.
 * @param {object} params.trip - The search trip.
 * @param {boolean} [params.isRoundTrip] - Whether the search is a round trip.
 * @param {string} [params.way] - `departure` or `return`.
 * @param {object} [params.departureTrip] - The departure the user picked, on a return.
 * @param {boolean} [params.validateRoundTripDiscount] - `VALIDATE_ROUND_TRIP_DISCOUNT`.
 * @param {object} [params.listPricing] - The pricing the list resolved for this card, if any.
 * @returns {object} `listPricing`, or `pricing`, `departureRoundTripPricing` or
 *   `roundTripPricing`.
 */
export const selectTripItemPricing = ({
  trip,
  isRoundTrip,
  way,
  departureTrip,
  validateRoundTripDiscount,
  listPricing,
}) => {
  if (listPricing) return listPricing;
  if (!isRoundTrip) return trip.pricing;
  if (way === 'departure') return trip.departureRoundTripPricing;

  if (
    validateRoundTripDiscount &&
    // ClosedTripsSection defaults departureTrip to null and ProviderReturns renders it without
    // one, so this must tolerate its absence rather than dereference it.
    !roundTripFareApplies({ departureLeg: departureTrip, returnLeg: trip })
  ) {
    // The return leg only earns the round-trip fare when the provider actually sells both legs.
    // TripList already resolves this the same way to pick the row's price; recomputing it here
    // without the check overrode that decision and advertised a fare the lock will not honor
    // (e.g. a Primera Plus return combined with an ETN departure showed 707 and charged 746).
    return trip.pricing;
  }

  return trip.roundTripPricing;
};

/**
 * Generic passenger-type candidates whose visible total must come from the applicable
 * pricing block on a canonical BFO leg (RM-1201): the root passenger-type total is the
 * generic one-way fare, not the leg's effective fare.
 * @param {Array} candidates - Searched passenger-type candidates.
 * @param {Object} pricing - Applicable pricing block for the card.
 * @returns {Array} Candidates with the generic fare sourced from the pricing block.
 */
const sourceGenericFareFromBfoPricing = (candidates, pricing) => {
  if (!isBfoPricing(pricing) || pricing.total == null) return candidates;
  return candidates.map((candidate) => {
    const isGenericFare =
      candidate.type === DEFAULT_PASSENGER_CATEGORY ||
      candidate.type === OVERBOOKED_PASSENGER_CATEGORY;
    return isGenericFare ? { ...candidate, total: pricing.total } : candidate;
  });
};

/**
 * The offer a card paints: the cheapest available one among the provider discounts, the flat-fare
 * discount and the searched passenger types, compared by `total`.
 *
 * The comparison stays on `total` in every arm (INS-1288 D2, BOOK-168 KTD5): an experiment changes
 * what is shown, never which offer wins.
 *
 * @param {object} params - Selection inputs.
 * @param {object} params.pricing - The leg pricing (`selectTripItemPricing`).
 * @param {Array<object>} [params.passengerTypes] - The trip's passenger-type fares.
 * @param {Array<string>} params.typesToFilter - Passenger types eligible for the pick.
 * @param {boolean} [params.showFlatFare] - Whether the flat-fare discount applies to this trip.
 * @param {boolean} [params.useFlatFareAsResultPrice] - `USE_FLAT_FARE_AS_RESULT_PRICE`.
 * @param {boolean} [params.isClosed] - Whether the trip is closed (sold out or departed).
 * @returns {{ lowestAvailablePrice: (object|null), displayedCandidate: (object|null),
 *   isCategoryPrice: boolean }} The winner, the object the card paints (the leg pricing itself
 *   for a closed trip) and whether the winner is a searched passenger category.
 */
export const selectTripItemCandidate = ({
  pricing,
  passengerTypes,
  typesToFilter,
  showFlatFare,
  useFlatFareAsResultPrice,
  isClosed,
}) => {
  const { providerDiscounts } = pricing;

  // Extract flat_fare from providerDiscounts
  const flatFareDiscount =
    providerDiscounts?.find((discount) => discount.name?.includes('flat_fare')) ?? null;

  // Check if there are other discounts besides flat_fare
  const hasOtherDiscounts = providerDiscounts?.some(
    (discount) => !discount.name?.includes('flat_fare'),
  );

  // Determine if flat fare should be included in price calculation
  // using flat fare if is the only discount available
  const shouldIncludeFlatFare =
    showFlatFare && flatFareDiscount && (useFlatFareAsResultPrice || !hasOtherDiscounts);

  // Find lowest available price from all valid discounts and searched passenger types
  const lowestAvailablePrice = [
    // Flat fare discount (only if conditions are met)
    ...(shouldIncludeFlatFare ? [flatFareDiscount] : []),
    // Provider discounts (excluding flat fare)
    ...(providerDiscounts?.filter((d) => !d.name?.includes('flat_fare')) || []),
    // Passenger types that were searched (or fallback to general/oversold)
    ...sourceGenericFareFromBfoPricing(
      passengerTypes?.filter((t) => typesToFilter.includes(t.type)) || [],
      pricing,
    ),
  ]
    .filter((d) => d.availability > 0)
    .reduce(
      (lowest, current) =>
        Number(current.total) < Number(lowest?.total || Infinity) ? current : lowest,
      null,
    );

  // The displayed price comes from a searched passenger category, not a provider discount
  const isCategoryPrice =
    Boolean(lowestAvailablePrice?.type) &&
    lowestAvailablePrice.type !== DEFAULT_PASSENGER_CATEGORY &&
    lowestAvailablePrice.type !== OVERBOOKED_PASSENGER_CATEGORY;

  return {
    lowestAvailablePrice,
    displayedCandidate: isClosed ? pricing : lowestAvailablePrice,
    isCategoryPrice,
  };
};

/**
 * The card props the final arm replaces (BOOK-168): each one is the markup-inclusive companion of
 * the figure control paints at that spot, so no operand of a strikethrough, discount or table-fare
 * comparison comes from the base arm (design D5):
 * - `totalBase` becomes `totalBaseWithMarkup`;
 * - the previous price reads `totalBeforeDiscountWithMarkup` (`resolveStrikethroughTotal`);
 * - the discount amount is `totalBeforeDiscountWithMarkup - totalWithMarkup`, and a backend
 *   percent is shown as sent;
 * - the table fare is shifted by the leg's markup, the counterpart of INS-1288 netting it, so the
 *   saving against it equals control's (KD8). It stays absent when the provider sends none.
 *
 * A missing companion falls back to the base field (`utils/trips/finalPricing`), so the worst case
 * is control's figure, never `NaN`.
 *
 * @param {object} params - The inputs of `resolveTripItemPrices` this arm reads.
 * @param {object} params.pricing - The leg pricing.
 * @param {boolean} params.showTotalBase - Whether the card receives `totalBase`.
 * @param {object} params.listFigures - What the list handed the card.
 * @returns {object} The card props the final arm replaces.
 */
const resolveFinalCardOverrides = ({ pricing, showTotalBase, listFigures }) => {
  const { providerDiscount } = pricing;
  // Taken off the leg pricing, like INS-1288's `netFeeDelta`: the table fare is the leg's.
  const markup = markupDelta(pricing);

  return {
    ...(showTotalBase && { totalBase: finalTotalBase(pricing) }),
    // Replaces TripList's `totalBeforeDiscount || amount + total`, whose operands are base. Read
    // off the card's own leg pricing: a coupon-swapped object from TripList carries no companion.
    ...(listFigures.totalBeforeDiscount != null && {
      totalBeforeDiscount: resolveStrikethroughTotal(pricing, pricing, providerDiscount),
    }),
    // Same `!= null` guard as INS-1288's: an absent table fare stays absent, a closed card
    // (which receives none) never gains one.
    ...(markup > 0 &&
      listFigures.officePrice != null && {
        officePrice: Number(listFigures.officePrice) + markup,
      }),
    // The cards derive their badge from `providerDiscount.amount` next to `total`; `percent`
    // and every other field ride along untouched.
    ...(listFigures.providerDiscount != null && {
      providerDiscount: {
        ...listFigures.providerDiscount,
        amount: discountAmountForArm(
          pricing,
          listFigures.providerDiscount.amount,
          PRICE_ARMS.FINAL,
        ),
      },
    }),
  };
};

/**
 * Every figure a card paints for the arm its readers resolved.
 *
 * `cardOverrides` holds only the props the arm has to replace; a key the card did not already
 * receive is never introduced (a closed card gets no previous price or table fare from
 * `ClosedTripsSection`, and must not gain one here).
 *
 * @param {object} params - Derivation inputs.
 * @param {object} params.pricing - The leg pricing (`selectTripItemPricing`).
 * @param {object|null} params.displayedCandidate - What the card paints
 *   (`selectTripItemCandidate`).
 * @param {string} [params.arm] - One of `PRICE_ARMS`.
 * @param {boolean} [params.showTotalBase] - Whether the card receives `totalBase` (logged-in or
 *   exchange users).
 * @param {object} [params.listFigures] - What the list handed the card, all absent for a closed
 *   card: `totalBeforeDiscount` (the previous price), `officePrice` (the ticket-office fare) and
 *   `providerDiscount` (the applied provider discount).
 * @returns {{ priceTotal: number, windowTicketPrice: number, cardOverrides: object }} The headline
 *   total, the pre-discount window price and the card props the arm replaces.
 */
export const resolveTripItemPrices = ({
  pricing,
  displayedCandidate,
  arm = PRICE_ARMS.CONTROL,
  showTotalBase = false,
  listFigures = {},
}) => {
  const { totalBase, discountBase, providerDiscount } = pricing;
  // One formula in every arm, which only picks the operands: the candidate's figure for the arm,
  // and the discount on that figure's scale (only the final arm rescales it).
  const priceTotal = Number(priceForArm(displayedCandidate, arm)) || 0;
  const discountAmount = providerDiscount ? providerDiscount.amount : 0;
  const discount = discountAmountForArm(pricing, discountBase || discountAmount, arm);
  const windowTicketPrice = priceTotal + discount;

  if (arm === PRICE_ARMS.FINAL) {
    return {
      priceTotal,
      windowTicketPrice,
      cardOverrides: resolveFinalCardOverrides({ pricing, showTotalBase, listFigures }),
    };
  }

  // How much the backend netted out of this trip's unit price, or 0 when nothing is netted --
  // 0 for every arm but netted. Used to keep every other figure on the card on the same scale
  // as the price above.
  //
  // Derived from `pricing`, NOT from the winning candidate: the three figures below
  // (`totalBase`, `totalBeforeDiscount`, `officePrice`) all come off `pricing`, so the delta
  // has to be on that same scale. Taking it from the candidate misread every trip whose reduce
  // was won by a non-adult fare -- a gratuidade (`total: 0`) produced no delta at all and left a
  // gross crossed-out price over a netted card, and a half fare produced about half the real one.
  const netFeeDelta = netFeePerPassenger(pricing, arm === PRICE_ARMS.NETTED);

  const cardOverrides = {
    // `totalBase` is read straight off `pricing`, so it never went through `netPricingView`;
    // TripResult promotes it to the primary price for logged users, which would show them the
    // gross fare above a netted seat panel.
    ...(showTotalBase && {
      totalBase:
        netFeeDelta > 0 && totalBase != null
          ? Math.max(Number(totalBase) - netFeeDelta, 0)
          : totalBase,
    }),
    // Keep the crossed-out "before" price on the same scale as the price above it: pairing a
    // netted total with a gross previous price invents a discount that does not exist -- the
    // same defect that rendered a gross "1 Passageiro" line over a netted seat-panel total.
    //
    // The `!= null` guard mirrors the officePrice one below, and is not defensive: a closed card
    // reaches here without `totalBeforeDiscount` at all (`ClosedTripsSection` never passes it)
    // while `netFeeDelta` can still be positive, and `Number(undefined ?? 0) - delta` clamped at 0
    // would introduce the key with a 0 where it previously did not exist (@luisAzcuaga).
    ...(netFeeDelta > 0 &&
      listFigures.totalBeforeDiscount != null && {
        totalBeforeDiscount: Math.max(Number(listFigures.totalBeforeDiscount) - netFeeDelta, 0),
      }),
    // Same reasoning for the window price SimpleTripResult crosses out under
    // SHOW_EARLY_DISCOUNTS: it reaches the card raw from `pricing` and is compared against
    // the netted `total` above, which would invent a discount that does not exist.
    ...(netFeeDelta > 0 &&
      listFigures.officePrice != null && {
        officePrice: Math.max(Number(listFigures.officePrice) - netFeeDelta, 0),
      }),
  };

  return { priceTotal, windowTicketPrice, cardOverrides };
};
